/**
 * KnownResultsScreen
 *
 * Asks if the user already has test results (AMH, FSH, SA).
 * Goal-specific — shows relevant tests only.
 */

import React, { useState } from "react";
import { View, Text, Pressable, ScrollView, TextInput } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { KnownResults } from "../types";

const SA_OPTIONS: {
  id: "normal" | "borderline" | "low" | "unknown";
  labelKey: string;
}[] = [
  { id: "normal", labelKey: "plan_builder.known_results.sa.normal" },
  { id: "borderline", labelKey: "plan_builder.known_results.sa.borderline" },
  { id: "low", labelKey: "plan_builder.known_results.sa.low" },
  { id: "unknown", labelKey: "plan_builder.known_results.sa.unknown" },
];

type Props = {
  onNext: () => void;
};

/** Goals where ovarian reserve tests are relevant (AFAB only) */
const OVARIAN_GOALS = new Set(["preserve", "embryo-preserve", "pregnancy"]);

/** Goals where SA is relevant (partner SA for these, or self SA for preserve+AMAB) */
const SA_GOALS = new Set(["embryo-preserve", "pregnancy", "sperm-donation"]);

export default function KnownResultsScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("KnownResultsScreen");
  const { state, updateState } = usePlanBuilder();
  const [results, setResults] = useState<KnownResults>(state.knownResults);

  // Gates mirror buildPlanBuilderStack so the step's presence and the field
  // rendering agree. Ovarian tests are AFAB-only; AMAB sperm-freezing
  // (preserve + male) is offered self semen analysis.
  const showOvarian =
    state.goal != null &&
    OVARIAN_GOALS.has(state.goal) &&
    state.gender !== "male";
  const showSA =
    state.goal != null &&
    (SA_GOALS.has(state.goal) ||
      (state.goal === "preserve" && state.gender === "male"));

  const update = (partial: Partial<KnownResults>) => {
    const updated = { ...results, ...partial };
    setResults(updated);
    updateState({ knownResults: updated });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.known_results.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.known_results.subtitle")}
      </Text>

      {/* AMH */}
      {showOvarian && (
        <View
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.82)",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: results.hasAMH
              ? EMBER.colors.text
              : "rgba(255, 255, 255, 0.9)",
            padding: 16,
            marginBottom: 12,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.04,
            shadowRadius: 8,
          }}
        >
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("plan_builder.known_results.amh_label")),
                "known_results_amh_toggle",
              );
              update({ hasAMH: !results.hasAMH });
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 15,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {t("plan_builder.known_results.amh_label")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "400",
                    color: EMBER.colors.textLight,
                    marginTop: 2,
                  }}
                >
                  {t("plan_builder.known_results.amh_detail")}
                </Text>
              </View>
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  borderWidth: 2,
                  borderColor: results.hasAMH
                    ? EMBER.colors.text
                    : EMBER.colors.border,
                  backgroundColor: results.hasAMH
                    ? EMBER.colors.text
                    : "transparent",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {results.hasAMH && (
                  <Text
                    style={{ color: "#FFF", fontSize: 12, fontWeight: "700" }}
                  >
                    {"\u2713"}
                  </Text>
                )}
              </View>
            </View>
          </Pressable>
          {results.hasAMH && (
            <View style={{ marginTop: 12 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "500",
                  color: EMBER.colors.textMid,
                  marginBottom: 6,
                }}
              >
                {t("plan_builder.known_results.amh_unit")}
              </Text>
              <TextInput
                className="font-dm-sans"
                style={{
                  borderWidth: 1,
                  borderColor: EMBER.colors.border,
                  borderRadius: 8,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  fontSize: 16,
                  color: EMBER.colors.text,
                }}
                keyboardType="decimal-pad"
                placeholder={t("plan_builder.known_results.amh_placeholder")}
                placeholderTextColor={EMBER.colors.textFaint}
                value={results.amhValue?.toString() ?? ""}
                onChangeText={(text) => {
                  const num = parseFloat(text);
                  update({ amhValue: isNaN(num) ? undefined : num });
                }}
              />
            </View>
          )}
        </View>
      )}

      {/* FSH */}
      {showOvarian && (
        <View
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.82)",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: results.hasFSH
              ? EMBER.colors.text
              : "rgba(255, 255, 255, 0.9)",
            padding: 16,
            marginBottom: 12,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.04,
            shadowRadius: 8,
          }}
        >
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("plan_builder.known_results.fsh_label")),
                "known_results_fsh_toggle",
              );
              update({ hasFSH: !results.hasFSH });
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 15,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {t("plan_builder.known_results.fsh_label")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "400",
                    color: EMBER.colors.textLight,
                    marginTop: 2,
                  }}
                >
                  {t("plan_builder.known_results.fsh_detail")}
                </Text>
              </View>
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  borderWidth: 2,
                  borderColor: results.hasFSH
                    ? EMBER.colors.text
                    : EMBER.colors.border,
                  backgroundColor: results.hasFSH
                    ? EMBER.colors.text
                    : "transparent",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {results.hasFSH && (
                  <Text
                    style={{ color: "#FFF", fontSize: 12, fontWeight: "700" }}
                  >
                    {"\u2713"}
                  </Text>
                )}
              </View>
            </View>
          </Pressable>
          {results.hasFSH && (
            <View style={{ marginTop: 12 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "500",
                  color: EMBER.colors.textMid,
                  marginBottom: 6,
                }}
              >
                {t("plan_builder.known_results.fsh_unit")}
              </Text>
              <TextInput
                className="font-dm-sans"
                style={{
                  borderWidth: 1,
                  borderColor: EMBER.colors.border,
                  borderRadius: 8,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  fontSize: 16,
                  color: EMBER.colors.text,
                }}
                keyboardType="decimal-pad"
                placeholder={t("plan_builder.known_results.fsh_placeholder")}
                placeholderTextColor={EMBER.colors.textFaint}
                value={results.fshValue?.toString() ?? ""}
                onChangeText={(text) => {
                  const num = parseFloat(text);
                  update({ fshValue: isNaN(num) ? undefined : num });
                }}
              />
            </View>
          )}
        </View>
      )}

      {/* Semen Analysis */}
      {showSA && (
        <View
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.82)",
            borderRadius: 14,
            borderWidth: 1,
            borderColor: results.hasSA
              ? EMBER.colors.text
              : "rgba(255, 255, 255, 0.9)",
            padding: 16,
            marginBottom: 12,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.04,
            shadowRadius: 8,
          }}
        >
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("plan_builder.known_results.sa_label")),
                "known_results_sa_toggle",
              );
              update({ hasSA: !results.hasSA });
            }}
          >
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <View>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 15,
                    fontWeight: "600",
                    color: EMBER.colors.text,
                  }}
                >
                  {t("plan_builder.known_results.sa_label")}
                </Text>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    fontWeight: "400",
                    color: EMBER.colors.textLight,
                    marginTop: 2,
                  }}
                >
                  {t("plan_builder.known_results.sa_detail")}
                </Text>
              </View>
              <View
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  borderWidth: 2,
                  borderColor: results.hasSA
                    ? EMBER.colors.text
                    : EMBER.colors.border,
                  backgroundColor: results.hasSA
                    ? EMBER.colors.text
                    : "transparent",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {results.hasSA && (
                  <Text
                    style={{ color: "#FFF", fontSize: 12, fontWeight: "700" }}
                  >
                    {"\u2713"}
                  </Text>
                )}
              </View>
            </View>
          </Pressable>
          {results.hasSA && (
            <View style={{ marginTop: 12 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "500",
                  color: EMBER.colors.textMid,
                  marginBottom: 8,
                }}
              >
                {t("plan_builder.known_results.sa_overall_label")}
              </Text>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {SA_OPTIONS.map((opt) => {
                  const isSelected = results.saStatus === opt.id;
                  return (
                    <Pressable
                      key={opt.id}
                      onPress={() => {
                        capturePress(pressLabel(t(opt.labelKey)), opt.id);
                        update({ saStatus: opt.id });
                      }}
                    >
                      <View
                        style={{
                          paddingHorizontal: 12,
                          paddingVertical: 7,
                          borderRadius: 8,
                          backgroundColor: isSelected
                            ? `${EMBER.colors.text}15`
                            : `${EMBER.colors.textFaint}12`,
                          borderWidth: 1,
                          borderColor: isSelected
                            ? EMBER.colors.text
                            : "transparent",
                        }}
                      >
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 13,
                            fontWeight: isSelected ? "600" : "400",
                            color: isSelected
                              ? EMBER.colors.text
                              : EMBER.colors.textMid,
                          }}
                        >
                          {t(opt.labelKey)}
                        </Text>
                      </View>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          )}
        </View>
      )}

      {/* Continue — always available (results are optional) */}
      <View style={{ marginTop: 16 }}>
        <WarmButton title={t("plan_builder.continue")} onPress={onNext} />
      </View>

      {/* Skip */}
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(t("plan_builder.known_results.skip")),
            "plan_builder.known_results.skip",
          );
          onNext();
        }}
        style={{ marginTop: 8 }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            fontWeight: "500",
            color: EMBER.colors.textLight,
            textAlign: "center",
            paddingVertical: 8,
          }}
        >
          {t("plan_builder.known_results.skip")}
        </Text>
      </Pressable>
    </ScrollView>
  );
}
